import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { Plus } from "lucide-react";

const faqs = [
  {
    q: "How is Core Scale different from a typical marketing agency?",
    a: "We don't run ads or post on social media for you. We engineer the underlying systems — AI agents, automations, funnels, and integrations — that make every marketing dollar convert. Think of us as your fractional AI engineering team, not a creative shop.",
  },
  {
    q: "How fast can the AI receptionist go live?",
    a: "Most receptionists are configured, voice-tuned, and live within 1–2 weeks. We handle the call routing, CRM hookup, calendar integration, and QA — you approve the script and we ship.",
  },
  {
    q: "Will the AI sound like a robot to my customers?",
    a: "No. We use the latest voice models and tune them to your brand tone, scripts, and call flows. The goal is a calm, human-sounding assistant that's faster and more consistent than your best receptionist.",
  },
  {
    q: "What tools and CRMs do you integrate with?",
    a: "GoHighLevel, HubSpot, Salesforce, Pipedrive, Zoho, Airtable, Google Calendar, Outlook, Stripe, Twilio, Slack, Zapier, Make, n8n — and most modern APIs. If it has a webhook, we can wire it.",
  },
  {
    q: "What does engagement look like — retainer or project?",
    a: "Both. Most clients start with a one-time Build & Integrate engagement for a specific system, then move into an Optimize & Scale retainer for ongoing iteration. We'll recommend the right fit on the strategy call.",
  },
  {
    q: "Do I own the systems you build?",
    a: "Yes — everything runs on your accounts, your data, your domain. We hand over full access and documentation. No lock-in.",
  },
  {
    q: "What industries do you work with?",
    a: "Service-based businesses where leads, calls, and bookings drive revenue: medical, dental, legal, real estate, home services, med spas, B2B services, and select e-commerce. If inbound matters to your business, we can help.",
  },
  {
    q: "How do we get started?",
    a: "Book a 30-minute strategy call. We'll map your current operations, identify the highest-leverage automation, and quote a system. No pressure — most calls end with a clear plan you can act on, with or without us.",
  },
];

export default function FAQ() {
  return (
    <section
      id="faq"
      className="relative section-padding bg-[#070707] border-y border-white/5"
      data-testid="faq-section"
    >
      <div className="absolute inset-0 dot-bg opacity-25 pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 mb-10">
          <div className="lg:col-span-5 reveal">
            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-[#39FF14]">
              <span className="w-6 h-px bg-[#39FF14]" />
              FAQ
            </div>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-white">
              Common <span className="gradient-text-green">questions.</span>
            </h2>
            <p className="mt-5 text-zinc-400 leading-relaxed">
              The fastest path to clarity — and to a 30-minute strategy call.
            </p>
            <a
              href="https://cal.com/erik-carlson/secret"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-7 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#39FF14] text-black font-semibold text-sm hover:bg-white transition-all hover:shadow-[0_0_28px_rgba(57,255,20,0.45)]"
              data-testid="faq-cta"
            >
              Ask in a strategy call
            </a>
          </div>

          <div className="lg:col-span-7 reveal">
            <Accordion type="single" collapsible className="w-full space-y-3">
              {faqs.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="rounded-2xl border border-white/8 bg-white/[0.015] px-5 transition-colors data-[state=open]:border-[#39FF14]/25 data-[state=open]:bg-white/[0.03]"
                  data-testid={`faq-item-${i + 1}`}
                >
                  <AccordionTrigger className="text-sm sm:text-base text-white hover:no-underline py-4 sm:py-5 [&>svg]:hidden group">
                    <span className="text-left pr-3 sm:pr-4 flex-1">{f.q}</span>
                    <span className="ml-2 shrink-0 inline-flex w-7 h-7 rounded-full border border-white/10 items-center justify-center text-zinc-400 group-hover:text-[#39FF14] group-hover:border-[#39FF14]/40 transition-all group-data-[state=open]:bg-[#39FF14] group-data-[state=open]:text-black group-data-[state=open]:border-[#39FF14] group-data-[state=open]:rotate-45 duration-300">
                      <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-zinc-400 leading-relaxed pb-5 pr-8 sm:pr-12">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
}
